import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Header, KpiCard, Tendencia, EstadoCarga, todayISO, addDays, fmtFecha } from "../analisis/comun";
import { nombrePantalla } from "../../utils/pantallasApp";

// ═══════════════════════════════════════════════════════════════════════════
// Panel "Uso de la app" (superadmin). Datos: GET /api/uso/resumen.
// Colores de grupo: paleta categórica validada (orden fijo, nunca por rango).
// Tiempo = tiempo ACTIVO (app visible + interacción en los últimos 2 min).
// ═══════════════════════════════════════════════════════════════════════════

const GRUPO_COLOR = {
  Asistentas: "#2a78d6", Supervisoras: "#eb6834", Compradores: "#1baf7a",
  Repartidores: "#eda100", "Administración": "#e87ba4",
};
const TXT = { primario: "#0f172a", secundario: "#475569", tenue: "#94a3b8", rejilla: "#e2e8f0" };
const TIPO_LABEL = {
  asistenta: "Asistentas", supervisora: "Supervisoras", comprador: "Compradores", repartidor: "Repartidores",
  superadmin: "Superadmin", admin_compras: "Admin compras", admin_servicios: "Admin servicios",
  admin_asistentas: "Admin asistentas", admin_atcliente: "Admin at. cliente", contabilidad: "Contabilidad",
  llaves: "Llaves", admin_llaves: "Admin llaves",
};
const DIAS_SEM = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const PRESETS = [{ k: 1, label: "Hoy" }, { k: 7, label: "7 días" }, { k: 30, label: "30 días" }, { k: 90, label: "90 días" }, { k: "custom", label: "Personalizado" }];

const fmtMin = (m) => {
  if (m == null) return "—";
  const v = Math.round(m);
  if (v < 60) return `${v} min`;
  const h = Math.floor(v / 60), r = v % 60;
  return r ? `${h} h ${r} min` : `${h} h`;
};
const pctDelta = (a, b) => (b ? Math.round(((a - b) / b) * 100) : null);
const haceDias = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
const fmtUltimo = (iso) => {
  if (!iso) return "Nunca";
  const d = haceDias(iso);
  if (d <= 0) return `Hoy ${iso.slice(11, 16)}`;
  if (d === 1) return "Ayer";
  return `Hace ${d} días`;
};

// Tooltip flotante común (una sola instancia por gráfica)
const Tooltip = ({ tip }) =>
  tip ? (
    <div style={{
      position: "fixed", left: Math.min(tip.x + 14, window.innerWidth - 230), top: tip.y + 14, zIndex: 1000,
      background: "#0f172a", color: "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 12, lineHeight: 1.45,
      pointerEvents: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.25)", maxWidth: 220,
    }}>
      {tip.lines.map((l, i) => <div key={i} style={i === 0 ? { fontWeight: 700, marginBottom: 2 } : undefined}>{l}</div>)}
    </div>
  ) : null;

const Tarjeta = ({ titulo, sub, children, derecha }) => (
  <div style={{ background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(15,23,42,0.06)", padding: "16px 18px" }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: TXT.primario }}>{titulo}</div>
        {sub && <div style={{ fontSize: 12, color: TXT.secundario, marginTop: 2 }}>{sub}</div>}
      </div>
      {derecha}
    </div>
    {children}
  </div>
);

const Leyenda = ({ grupos, activos, onToggle }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
    {grupos.map((g) => (
      <button key={g} onClick={() => onToggle && onToggle(g)} style={{
        display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: TXT.secundario, background: "none",
        border: "none", cursor: onToggle ? "pointer" : "default", opacity: !activos || activos.has(g) ? 1 : 0.35, padding: 0,
      }}>
        <span style={{ width: 14, height: 3, borderRadius: 2, background: GRUPO_COLOR[g] }} />{g}
      </button>
    ))}
  </div>
);

// ── Evolución: usuarios activos por día y grupo (líneas + cruceta) ─────────
function Evolucion({ dias, evolucion }) {
  const [tip, setTip] = useState(null);
  const [ocultos, setOcultos] = useState(new Set());
  const W = 720, H = 220, PL = 34, PR = 96, PT = 10, PB = 26;
  // % de usuarios del grupo activos cada día: compara grupos de tamaño muy distinto
  const series = evolucion.filter((s) => s.total > 0 && s.valores.some((v) => v > 0))
    .map((s) => ({ ...s, pct: s.valores.map((v) => Math.round((v / s.total) * 100)) }));
  const visibles = series.filter((s) => !ocultos.has(s.grupo));
  const max = 100;
  const x = (i) => PL + (dias.length <= 1 ? 0 : (i * (W - PL - PR)) / (dias.length - 1));
  const y = (v) => PT + (H - PT - PB) * (1 - v / max);
  const ticks = [0, 25, 50, 75, 100];
  const cada = Math.max(1, Math.ceil(dias.length / 8));
  const [hover, setHover] = useState(null);
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.max(0, Math.min(dias.length - 1, Math.round(((px - PL) / (W - PL - PR)) * (dias.length - 1))));
    setHover(i);
    setTip({ x: e.clientX, y: e.clientY, lines: [fmtFecha(dias[i]), ...visibles.map((s) => `${s.grupo}: ${s.pct[i]}% (${s.valores[i]} de ${s.total})`)] });
  };
  if (!series.length) return <div style={{ color: TXT.tenue, fontSize: 13 }}>Sin datos en el periodo.</div>;
  // Un solo día (filtro "Hoy"): no hay evolución que dibujar → barras por grupo
  if (dias.length === 1) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {series.map((s) => (
          <div key={s.grupo} style={{ display: "grid", gridTemplateColumns: "120px 1fr 120px", gap: 10, alignItems: "center" }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: TXT.primario }}>{s.grupo}</div>
            <div style={{ background: "#f1f5f9", borderRadius: 4, height: 14 }}>
              <div style={{ width: `${Math.max(1.5, s.pct[0])}%`, height: 14, borderRadius: 4, background: GRUPO_COLOR[s.grupo] }} />
            </div>
            <div style={{ fontSize: 12, color: TXT.secundario }}>{s.pct[0]}% · {s.valores[0]} de {s.total}</div>
          </div>
        ))}
      </div>
    );
  }
  const toggle = (g) => setOcultos((o) => { const n = new Set(o); n.has(g) ? n.delete(g) : n.add(g); return n; });
  // etiquetas finales sin solaparse
  // etiqueta final = media del periodo (el último día puede estar a medias)
  const finales = visibles.map((s) => { const m = Math.round(s.pct.reduce((a, b) => a + b, 0) / s.pct.length); return { g: s.grupo, v: `${m}% media`, y: y(s.pct[s.pct.length - 1]) }; }).sort((a, b) => a.y - b.y);
  for (let i = 1; i < finales.length; i++) if (finales[i].y - finales[i - 1].y < 13) finales[i].y = finales[i - 1].y + 13;
  return (
    <>
      <Leyenda grupos={series.map((s) => s.grupo)} activos={new Set(series.map((s) => s.grupo).filter((g) => !ocultos.has(g)))} onToggle={toggle} />
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}
        onMouseMove={onMove} onMouseLeave={() => { setTip(null); setHover(null); }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={PL} x2={W - PR} y1={y(t)} y2={y(t)} stroke={TXT.rejilla} strokeWidth={1} />
            <text x={PL - 6} y={y(t) + 4} fontSize={10} fill={TXT.tenue} textAnchor="end">{t}%</text>
          </g>
        ))}
        {dias.map((d, i) => (i % cada === 0 || i === dias.length - 1) && (
          <text key={d} x={x(i)} y={H - 8} fontSize={10} fill={TXT.tenue} textAnchor="middle">{d.slice(8, 10)}/{d.slice(5, 7)}</text>
        ))}
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={PT} y2={H - PB} stroke={TXT.tenue} strokeDasharray="3 3" />}
        {visibles.map((s) => (
          <polyline key={s.grupo} fill="none" stroke={GRUPO_COLOR[s.grupo]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
            points={s.pct.map((v, i) => `${x(i)},${y(v)}`).join(" ")} />
        ))}
        {hover != null && visibles.map((s) => (
          <circle key={s.grupo} cx={x(hover)} cy={y(s.pct[hover])} r={4} fill={GRUPO_COLOR[s.grupo]} stroke="#fff" strokeWidth={2} />
        ))}
        {finales.map((f) => (
          <text key={f.g} x={W - PR + 8} y={f.y + 4} fontSize={11} fill={TXT.secundario}>{f.g} · {f.v}</text>
        ))}
      </svg>
      <Tooltip tip={tip} />
    </>
  );
}

// ── Mapa de calor: minutos activos por día de la semana × hora ─────────────
function MapaCalor({ heatmap }) {
  const [tip, setTip] = useState(null);
  const max = Math.max(1, ...heatmap.flat());
  const horas = [];
  for (let h = 0; h < 24; h++) if (heatmap.some((f) => f[h] > 0)) horas.push(h);
  const hIni = horas.length ? Math.max(0, horas[0]) : 7, hFin = horas.length ? horas[horas.length - 1] : 21;
  const rango = []; for (let h = hIni; h <= hFin; h++) rango.push(h);
  // rampa secuencial de un solo tono (claro → oscuro)
  const color = (v) => {
    if (!v) return "#f1f5f9";
    const t = Math.sqrt(v / max);
    const a = [234, 242, 252], b = [23, 78, 150];
    const c = a.map((ai, i) => Math.round(ai + (b[i] - ai) * t));
    return `rgb(${c.join(",")})`;
  };
  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ display: "grid", gridTemplateColumns: `36px repeat(${rango.length}, minmax(18px, 1fr))`, gap: 2, minWidth: 36 + rango.length * 20 }}>
        <div />
        {rango.map((h) => <div key={h} style={{ fontSize: 10, color: TXT.tenue, textAlign: "center" }}>{h}</div>)}
        {DIAS_SEM.map((d, di) => (
          <React.Fragment key={d}>
            <div style={{ fontSize: 11, color: TXT.secundario, fontWeight: 600, alignSelf: "center" }}>{d}</div>
            {rango.map((h) => {
              const v = heatmap[di][h];
              return (
                <div key={h}
                  onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, lines: [`${DIAS_SEM[di]} ${h}:00–${h + 1}:00`, `${fmtMin(v)} de uso activo`] })}
                  onMouseLeave={() => setTip(null)}
                  style={{ height: 22, borderRadius: 4, background: color(v), cursor: "default" }} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 11, color: TXT.tenue }}>
        Menos
        {[0.05, 0.25, 0.5, 0.75, 1].map((t) => <span key={t} style={{ width: 18, height: 10, borderRadius: 3, background: color(t * max) }} />)}
        Más
      </div>
      <Tooltip tip={tip} />
    </div>
  );
}

// ── Pantallas más usadas (barras horizontales) ────────────────────────────
function Pantallas({ pantallas }) {
  const [todas, setTodas] = useState(false);
  const [tip, setTip] = useState(null);
  const lista = todas ? pantallas : pantallas.slice(0, 12);
  const max = Math.max(1, ...pantallas.map((p) => p.minutos));
  const umbralPoco = max * 0.02;
  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {lista.map((p) => (
          <div key={p.pantalla} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 190px) 1fr 86px", alignItems: "center", gap: 10 }}
            onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, lines: [nombrePantalla(p.pantalla), `${fmtMin(p.minutos)} en total`, `${p.aperturas} aperturas · ${p.min_medio} min de media`, `${p.usuarios} usuarios distintos`] })}
            onMouseLeave={() => setTip(null)}>
            <div style={{ fontSize: 12.5, color: TXT.primario, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{nombrePantalla(p.pantalla)}</div>
            <div style={{ background: "#f1f5f9", borderRadius: 4, height: 12 }}>
              <div style={{ width: `${Math.max(1.5, (p.minutos / max) * 100)}%`, height: 12, borderRadius: 4, background: p.minutos < umbralPoco ? "#cbd5e1" : "#2a78d6" }} />
            </div>
            <div style={{ fontSize: 12, color: TXT.secundario, textAlign: "right" }}>{fmtMin(p.minutos)}</div>
          </div>
        ))}
      </div>
      {pantallas.length > 12 && (
        <button onClick={() => setTodas((t) => !t)} style={{ marginTop: 10, background: "none", border: "none", color: "#2563eb", fontWeight: 700, fontSize: 13, cursor: "pointer", padding: 0 }}>
          {todas ? "Ver solo las 12 primeras" : `Ver las ${pantallas.length} pantallas`}
        </button>
      )}
      <Tooltip tip={tip} />
    </>
  );
}

// Mini-gráfica de los días del periodo (barras finas)
const Sparkline = ({ serie, color }) => {
  const max = Math.max(1, ...serie);
  const w = 110, h = 26, bw = w / Math.max(serie.length, 1);
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      {serie.map((v, i) => v > 0 && (
        <rect key={i} x={i * bw + 0.5} y={h - (v / max) * h} width={Math.max(1, bw - 1)} height={(v / max) * h} rx={1} fill={color} />
      ))}
      <line x1={0} x2={w} y1={h - 0.5} y2={h - 0.5} stroke={TXT.rejilla} />
    </svg>
  );
};

// ── Quién usa más y menos, por rol ────────────────────────────────────────
function Ranking({ ranking, usuarios, onUsuario }) {
  const [abiertos, setAbiertos] = useState(new Set());
  const [sinUsoAbiertos, setSinUsoAbiertos] = useState(new Set());
  const conmutar = (set) => (t) => set((a) => { const n = new Set(a); n.has(t) ? n.delete(t) : n.add(t); return n; });
  const alternar = conmutar(setAbiertos);
  const alternarSinUso = conmutar(setSinUsoAbiertos);
  const Fila = ({ u, max }) => (
    <div onClick={() => onUsuario(u.user)} style={{ display: "grid", gridTemplateColumns: "minmax(90px, 1fr) 1.2fr 70px", gap: 8, alignItems: "center", cursor: "pointer", padding: "3px 0" }}>
      <div style={{ fontSize: 12.5, color: TXT.primario, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.nombre}</div>
      <div style={{ background: "#f1f5f9", borderRadius: 4, height: 10 }}>
        <div style={{ width: `${Math.max(2, (u.minutos / max) * 100)}%`, height: 10, borderRadius: 4, background: GRUPO_COLOR[u.grupo] }} />
      </div>
      <div style={{ fontSize: 11.5, color: TXT.secundario, textAlign: "right" }}>{fmtMin(u.minutos)}<span style={{ color: TXT.tenue }}> · {u.dias_activos}d</span></div>
    </div>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
      {ranking.map((r) => {
        const max = Math.max(1, ...r.top.map((u) => u.minutos));
        const abierto = abiertos.has(r.tipo);
        // todos los del rol, de más a menos uso (la lista completa ya viene en `usuarios`)
        const todos = usuarios.filter((u) => u.tipo === r.tipo && u.minutos > 0).sort((a, b) => b.minutos - a.minutos);
        return (
          <div key={r.tipo} style={{ border: `1.5px solid #eef2f7`, borderTop: `4px solid ${GRUPO_COLOR[r.grupo]}`, borderRadius: 12, padding: "12px 14px" }}>
            <div style={{ display: "flex", alignItems: "baseline", marginBottom: 8 }}>
              <div style={{ flex: 1, fontWeight: 800, fontSize: 14, color: TXT.primario }}>{TIPO_LABEL[r.tipo] || r.tipo}</div>
              <div style={{ fontSize: 11.5, color: TXT.secundario }}>{r.con_uso} de {r.usuarios} con uso</div>
            </div>
            {abierto ? (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: TXT.tenue, textTransform: "uppercase", letterSpacing: 0.4, margin: "4px 0" }}>Todos · de más a menos uso</div>
                <div style={{ maxHeight: 360, overflowY: "auto", paddingRight: 4 }}>
                  {todos.map((u, i) => (
                    <div key={u.user} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10.5, color: TXT.tenue, width: 22, textAlign: "right", flexShrink: 0 }}>{i + 1}</span>
                      <div style={{ flex: 1, minWidth: 0 }}><Fila u={u} max={max} /></div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
            <div style={{ fontSize: 11, fontWeight: 700, color: TXT.tenue, textTransform: "uppercase", letterSpacing: 0.4, margin: "4px 0" }}>▲ Más uso</div>
            {r.top.length ? r.top.map((u) => <Fila key={u.user} u={u} max={max} />) : <div style={{ fontSize: 12, color: TXT.tenue }}>—</div>}
            {r.bottom.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: TXT.tenue, textTransform: "uppercase", letterSpacing: 0.4, margin: "10px 0 4px" }}>▼ Menos uso</div>
                {r.bottom.map((u) => <Fila key={u.user} u={u} max={max} />)}
              </>
            )}
              </>
            )}
            {r.con_uso > 5 && (
              <button onClick={() => alternar(r.tipo)} style={{ marginTop: 8, background: "none", border: "none", color: "#2563eb", fontWeight: 700, fontSize: 12.5, cursor: "pointer", padding: 0 }}>
                {abierto ? "▲ Ver solo los 5 que más y menos" : `▼ Ver todos (${r.con_uso})`}
              </button>
            )}
            {r.sin_uso.length > 0 && (
              <div style={{ marginTop: 10, background: "#fef2f2", borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#b91c1c", marginBottom: 4 }}>⚠️ Sin uso en el periodo ({r.sin_uso.length})</div>
                <div style={{ fontSize: 12, color: "#7f1d1d", lineHeight: 1.6, maxHeight: 220, overflowY: "auto" }}>
                  {(sinUsoAbiertos.has(r.tipo) ? r.sin_uso : r.sin_uso.slice(0, 12)).map((u, i) => (
                    <React.Fragment key={u.user}>
                      {i > 0 && " · "}
                      <span onClick={() => onUsuario(u.user)} style={{ cursor: "pointer", textDecoration: "underline dotted" }} title="Ver ficha">{u.nombre}</span>
                    </React.Fragment>
                  ))}
                  {r.sin_uso.length > 12 && (
                    <button onClick={() => alternarSinUso(r.tipo)} style={{ marginLeft: 6, background: "none", border: "none", color: "#b91c1c", fontWeight: 800, fontSize: 12, cursor: "pointer", padding: 0 }}>
                      {sinUsoAbiertos.has(r.tipo) ? "▲ ver menos" : `· y ${r.sin_uso.length - 12} más ▼`}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tabla de usuarios ─────────────────────────────────────────────────────
function TablaUsuarios({ usuarios, onUsuario }) {
  const [orden, setOrden] = useState({ campo: "minutos", dir: -1 });
  const [busca, setBusca] = useState("");
  const cols = [
    { k: "nombre", label: "Usuario" }, { k: "tipo", label: "Rol" }, { k: "ultimo_acceso", label: "Último acceso" },
    { k: "dias_activos", label: "Días activos" }, { k: "minutos", label: "Tiempo total" }, { k: "min_medio_dia", label: "Media/día" },
    { k: "pantalla_favorita", label: "Pantalla favorita" }, { k: "serie", label: "Últimos días", noSort: true },
  ];
  const q = busca.trim().toLowerCase();
  const filas = usuarios
    .filter((u) => !q || u.nombre.toLowerCase().includes(q) || u.user.toLowerCase().includes(q))
    .slice()
    .sort((a, b) => {
      const va = a[orden.campo] ?? "", vb = b[orden.campo] ?? "";
      return (va > vb ? 1 : va < vb ? -1 : 0) * orden.dir;
    });
  const th = { padding: "8px 10px", fontSize: 11.5, color: TXT.secundario, fontWeight: 700, textAlign: "left", borderBottom: `2px solid ${TXT.rejilla}`, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
  const td = { padding: "7px 10px", fontSize: 12.5, color: TXT.primario, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" };
  return (
    <>
      <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="🔍 Buscar usuario…"
        style={{ width: "100%", maxWidth: 320, padding: "9px 12px", borderRadius: 10, border: `1.5px solid ${TXT.rejilla}`, fontSize: 13, marginBottom: 10, boxSizing: "border-box" }} />
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{cols.map((c) => (
              <th key={c.k} style={{ ...th, cursor: c.noSort ? "default" : "pointer" }}
                onClick={() => !c.noSort && setOrden((o) => ({ campo: c.k, dir: o.campo === c.k ? -o.dir : -1 }))}>
                {c.label}{orden.campo === c.k ? (orden.dir < 0 ? " ▼" : " ▲") : ""}
              </th>
            ))}</tr>
          </thead>
          <tbody>
            {filas.map((u) => {
              const dias = haceDias(u.ultimo_acceso);
              const alerta = u.ultimo_acceso == null || dias > 7;
              return (
                <tr key={u.user} onClick={() => onUsuario(u.user)} style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fafc")} onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                  <td style={{ ...td, fontWeight: 700 }}>
                    <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: GRUPO_COLOR[u.grupo], marginRight: 7 }} />
                    {u.nombre}
                  </td>
                  <td style={{ ...td, color: TXT.secundario }}>{TIPO_LABEL[u.tipo] || u.tipo || "—"}</td>
                  <td style={{ ...td, color: alerta ? "#b91c1c" : TXT.secundario, fontWeight: alerta ? 700 : 400 }}>{alerta ? "⚠️ " : ""}{fmtUltimo(u.ultimo_acceso)}</td>
                  <td style={td}>{u.dias_activos}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{fmtMin(u.minutos)}</td>
                  <td style={td}>{u.min_medio_dia ? fmtMin(u.min_medio_dia) : "—"}</td>
                  <td style={{ ...td, color: TXT.secundario, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{u.pantalla_favorita ? nombrePantalla(u.pantalla_favorita) : "—"}</td>
                  <td style={td}><Sparkline serie={u.serie} color={GRUPO_COLOR[u.grupo]} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── Ficha de un usuario (modal) ───────────────────────────────────────────
function FichaUsuario({ user, rango, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    setData(null); setError(false);
    axios.get("/api/uso/resumen", { params: { ...rango, user } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [user, rango]);
  const u = data?.usuarios?.find((x) => x.user === user);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 900, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "30px 12px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#f0f4f8", borderRadius: 16, width: "100%", maxWidth: 820, overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)", color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{u ? (TIPO_LABEL[u.tipo] || u.tipo) : "Usuario"}</div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>👤 {u?.nombre || user}</div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: "#fff", borderRadius: 10, padding: "8px 14px", fontWeight: 700, cursor: "pointer" }}>Cerrar ✕</button>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
          {error && <div style={{ color: "#b91c1c" }}>No se pudieron cargar los datos.</div>}
          {!data && !error && <div style={{ color: TXT.secundario }}>Cargando…</div>}
          {u && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                <KpiCard icon="⏱️" label="Tiempo activo" value={fmtMin(u.minutos)} sub={<Tendencia delta={pctDelta(u.minutos, u.minutos_anterior)} />} bg="#fff" />
                <KpiCard icon="📅" label="Días activos" value={u.dias_activos} sub={`de ${data.periodo.dias.length}`} bg="#fff" />
                <KpiCard icon="📊" label="Media por día activo" value={fmtMin(u.min_medio_dia)} bg="#fff" />
                <KpiCard icon="🔁" label="Sesiones" value={u.sesiones} bg="#fff" />
                <KpiCard icon={u.dispositivo === "pc" ? "💻" : "📱"} label="Dispositivo habitual" value={u.dispositivo === "pc" ? "Ordenador" : u.dispositivo === "movil" ? "Móvil" : "—"} sub={u.usa_app_instalada ? "Usa la app instalada" : "Desde el navegador"} bg="#fff" />
                <KpiCard icon="🕒" label="Último acceso" value={fmtUltimo(u.ultimo_acceso)} bg="#fff" />
              </div>
              <Tarjeta titulo="Tiempo activo por día" sub="Minutos de uso real cada día del periodo">
                <BarrasDia dias={data.periodo.dias} serie={u.serie} color={GRUPO_COLOR[u.grupo]} />
              </Tarjeta>
              <Tarjeta titulo="Cuándo usa la app" sub="Minutos activos por día de la semana y hora"><MapaCalor heatmap={data.heatmap} /></Tarjeta>
              <Tarjeta titulo="Sus pantallas"><Pantallas pantallas={data.pantallas} /></Tarjeta>
            </>
          )}
          {data && !u && <div style={{ color: TXT.secundario }}>Sin datos de uso para este usuario en el periodo.</div>}
        </div>
      </div>
    </div>
  );
}

function BarrasDia({ dias, serie, color }) {
  const [tip, setTip] = useState(null);
  const W = 720, H = 150, PB = 22, PL = 30;
  const max = Math.max(1, ...serie);
  const bw = (W - PL) / Math.max(1, dias.length);
  const cada = Math.max(1, Math.ceil(dias.length / 8));
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} onMouseLeave={() => setTip(null)}>
        {[0, Math.round(max / 2), max].map((t) => (
          <g key={t}>
            <line x1={PL} x2={W} y1={(H - PB) * (1 - t / max)} y2={(H - PB) * (1 - t / max)} stroke={TXT.rejilla} />
            <text x={PL - 5} y={(H - PB) * (1 - t / max) + 4} fontSize={10} fill={TXT.tenue} textAnchor="end">{t}</text>
          </g>
        ))}
        {serie.map((v, i) => (
          <g key={i} onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, lines: [fmtFecha(dias[i]), `${fmtMin(v)} activo`] })}>
            <rect x={PL + i * bw} y={0} width={bw} height={H - PB} fill="transparent" />
            {v > 0 && <rect x={PL + i * bw + 1} y={(H - PB) * (1 - v / max)} width={Math.max(1, bw - 2)} height={(H - PB) * (v / max)} rx={3} fill={color} />}
            {(i % cada === 0) && <text x={PL + i * bw + bw / 2} y={H - 6} fontSize={10} fill={TXT.tenue} textAnchor="middle">{dias[i].slice(8, 10)}/{dias[i].slice(5, 7)}</text>}
          </g>
        ))}
      </svg>
      <Tooltip tip={tip} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function UsoApp() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState(30);
  const [custom, setCustom] = useState({ desde: addDays(todayISO(), -29), hasta: todayISO() });
  const [grupo, setGrupo] = useState("");
  const [tipo, setTipo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fichaUser, setFichaUser] = useState(null);

  const rango = useMemo(() => (
    preset === "custom" ? custom : { desde: addDays(todayISO(), -(preset - 1)), hasta: todayISO() }
  ), [preset, custom]);

  const cargar = useCallback(() => {
    setLoading(true); setError(null);
    axios.get("/api/uso/resumen", { params: { ...rango, ...(grupo ? { grupo } : {}), ...(tipo ? { tipo } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error || "No se pudieron cargar los datos"))
      .finally(() => setLoading(false));
  }, [rango, grupo, tipo]);
  useEffect(() => { cargar(); }, [cargar]);

  const k = data?.kpis, ka = data?.kpis_anterior;
  const tiposDisponibles = useMemo(() => Object.keys(TIPO_LABEL), []);
  const sel = { padding: "8px 12px", borderRadius: 10, border: `1.5px solid ${TXT.rejilla}`, fontSize: 13, background: "#fff", color: TXT.primario };

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", fontFamily: "Segoe UI, sans-serif", paddingBottom: 40 }}>
      <Header icon="📈" title="Uso de la app" onBack={() => navigate(-1)}
        right={<div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.6)", textAlign: "right", maxWidth: 230 }}>Tiempo activo: solo cuenta con la app visible y en uso. Se para en segundo plano y, tras 2 min sin tocarla, esos 2 min no se cuentan</div>} />

      {/* Filtros: una sola fila */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", padding: "10px 16px", background: "#fff", borderBottom: `1px solid ${TXT.rejilla}` }}>
        {PRESETS.map((p) => (
          <button key={p.k} onClick={() => setPreset(p.k)} style={{
            padding: "7px 14px", borderRadius: 20, fontWeight: 600, fontSize: 13, cursor: "pointer",
            border: preset === p.k ? "1.5px solid #1e293b" : `1.5px solid ${TXT.rejilla}`,
            background: preset === p.k ? "#1e293b" : "#f8fafc", color: preset === p.k ? "#fff" : TXT.secundario,
          }}>{p.label}</button>
        ))}
        {preset === "custom" && (
          <>
            <input type="date" value={custom.desde} max={custom.hasta} onChange={(e) => setCustom((c) => ({ ...c, desde: e.target.value }))} style={sel} />
            <input type="date" value={custom.hasta} min={custom.desde} max={todayISO()} onChange={(e) => setCustom((c) => ({ ...c, hasta: e.target.value }))} style={sel} />
          </>
        )}
        <div style={{ flex: 1 }} />
        <select value={grupo} onChange={(e) => { setGrupo(e.target.value); setTipo(""); }} style={sel}>
          <option value="">Todos los grupos</option>
          {Object.keys(GRUPO_COLOR).map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={tipo} onChange={(e) => { setTipo(e.target.value); setGrupo(""); }} style={sel}>
          <option value="">Todos los roles</option>
          {tiposDisponibles.map((t) => <option key={t} value={t}>{TIPO_LABEL[t]}</option>)}
        </select>
      </div>

      <div style={{ padding: 16, maxWidth: 1180, margin: "0 auto", display: "flex", flexDirection: "column", gap: 14 }}>
        <EstadoCarga loading={loading && !data} error={error} vacio={!loading && !error && data && data.kpis.aperturas === 0} onRetry={cargar} />
        {data && k && (
          <>
            <div style={{ fontSize: 12.5, color: TXT.secundario }}>
              {fmtFecha(data.periodo.desde)} – {fmtFecha(data.periodo.hasta)} · comparado con {fmtFecha(data.anterior.desde)} – {fmtFecha(data.anterior.hasta)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
              <KpiCard icon="👥" label="Usuarios activos en el periodo" value={`${k.usuarios_activos} / ${k.usuarios_totales}`} sub={<Tendencia delta={pctDelta(k.usuarios_activos, ka.usuarios_activos)} />} bg="#fff" />
              <KpiCard icon="🟢" label="Activos hoy" value={k.activos_hoy} bg="#fff" />
              <KpiCard icon="⏱️" label="Tiempo medio por usuario y día" value={fmtMin(k.min_medio_dia)} sub={<Tendencia delta={pctDelta(k.min_medio_dia, ka.min_medio_dia)} />} bg="#fff" />
              <KpiCard icon="⌛" label="Horas de uso totales" value={`${k.horas_totales} h`} sub={<Tendencia delta={pctDelta(k.horas_totales, ka.horas_totales)} />} bg="#fff" />
              <KpiCard icon="⭐" label="Pantalla más usada" value={<span style={{ fontSize: 15 }}>{data.pantalla_top ? nombrePantalla(data.pantalla_top) : "—"}</span>} bg="#fff" />
              <KpiCard icon="⚠️" label="Sin entrar más de 7 días" value={k.sin_entrar_7d} sub={`de ${k.usuarios_totales} usuarios`} color={k.sin_entrar_7d ? "#b91c1c" : TXT.primario} bg={k.sin_entrar_7d ? "#fef2f2" : "#fff"} />
            </div>

            <Tarjeta titulo="Usuarios activos por día" sub="% de usuarios de cada grupo que usaron la app ese día · pulsa la leyenda para ocultar un grupo">
              <Evolucion dias={data.periodo.dias} evolucion={data.evolucion} />
            </Tarjeta>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 14 }}>
              <Tarjeta titulo="Cuándo se usa la app" sub="Minutos de uso activo por día de la semana y hora">
                <MapaCalor heatmap={data.heatmap} />
              </Tarjeta>
              <Tarjeta titulo="Pantallas más usadas" sub="Por tiempo activo total · en gris, las casi sin uso">
                <Pantallas pantallas={data.pantallas} />
              </Tarjeta>
            </div>

            <Tarjeta titulo="Quién usa más y menos la app" sub="Por rol · tiempo activo y días con uso · pulsa un nombre para ver su ficha">
              <Ranking ranking={data.ranking} usuarios={data.usuarios} onUsuario={setFichaUser} />
            </Tarjeta>

            <Tarjeta titulo="Todos los usuarios" sub={`${data.usuarios.length} usuarios · pulsa una columna para ordenar y una fila para ver la ficha`}
              derecha={<div style={{ fontSize: 12, color: TXT.secundario }}>📱 {data.dispositivos.movil} visitas desde móvil · 💻 {data.dispositivos.pc} desde ordenador · {data.dispositivos.usuarios_app_instalada} con la app instalada</div>}>
              <TablaUsuarios usuarios={data.usuarios} onUsuario={setFichaUser} />
            </Tarjeta>
          </>
        )}
      </div>
      {fichaUser && <FichaUsuario user={fichaUser} rango={rango} onClose={() => setFichaUser(null)} />}
    </div>
  );
}
